var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React from 'react';
const GOOGLE_PLACES_API_KEY_META = 'GOOGLE-PLACES-API-KEY';
const PLACES_LIBRARY = 'places';
const DEBOUNCE_MS = 250;
const INCLUDED_TYPES = ['street_address', 'premise', 'subpremise'];
const INCLUDED_REGIONS = ['us'];
const getApiKey = () => { var _a; return ((_a = document.querySelector(`meta[name="${GOOGLE_PLACES_API_KEY_META}"]`)) === null || _a === void 0 ? void 0 : _a.getAttribute('content')) || null; };
let sdkPromise = null;
const loadSdk = (apiKey) => {
    if (sdkPromise)
        return sdkPromise;
    sdkPromise = new Promise((resolve, reject) => {
        const existing = document.querySelector('script[data-google-maps]');
        if (existing) {
            existing.addEventListener('load', () => resolve(null));
            existing.addEventListener('error', reject);
            return;
        }
        const s = document.createElement('script');
        s.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&libraries=${PLACES_LIBRARY}&v=weekly`;
        s.async = true;
        s.defer = true;
        s.setAttribute('data-google-maps', '1');
        s.onload = () => resolve(null);
        s.onerror = reject;
        document.head.appendChild(s);
    });
    return sdkPromise;
};
const parseComponents = (comps) => {
    const get = (type, short = false) => { var _a, _b; return (_b = (_a = comps.find((c) => c.types.includes(type))) === null || _a === void 0 ? void 0 : _a[short ? 'shortText' : 'longText']) !== null && _b !== void 0 ? _b : ''; };
    const streetNumber = get('street_number');
    const route = get('route');
    const subpremise = get('subpremise');
    const street = [streetNumber, route].filter(Boolean).join(' ');
    return {
        address: subpremise ? `${street} #${subpremise}` : street,
        city: get('locality') || get('sublocality') || get('postal_town'),
        state: get('administrative_area_level_1', true),
        zip: get('postal_code'),
    };
};
export default (props) => {
    const id = React.useId();
    const [floating, setFloating] = React.useState(false);
    const [open, setOpen] = React.useState(false);
    const [suggestions, setSuggestions] = React.useState([]);
    const [loading, setLoading] = React.useState(false);
    const [ready, setReady] = React.useState(false);
    const containerRef = React.useRef(null);
    const sessionTokenRef = React.useRef(null);
    const debounceRef = React.useRef(null);
    const apiKey = React.useMemo(getApiKey, []);
    const enabled = !!apiKey && !props.disabled;
    React.useEffect(() => {
        if (!enabled)
            return;
        loadSdk(apiKey).then(() => setReady(true)).catch(() => setReady(false));
    }, [enabled, apiKey]);
    React.useEffect(() => {
        const onDocMouseDown = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target))
                setOpen(false);
        };
        document.addEventListener('mousedown', onDocMouseDown);
        return () => document.removeEventListener('mousedown', onDocMouseDown);
    }, []);
    const fetchSuggestions = (query) => __awaiter(void 0, void 0, void 0, function* () {
        if (!ready || !query.trim()) {
            setSuggestions([]);
            return;
        }
        const places = window.google.maps.places;
        if (!sessionTokenRef.current) {
            const Ctor = places.AutocompleteSessionToken;
            sessionTokenRef.current = new Ctor();
        }
        const service = places.AutocompleteSuggestion;
        setLoading(true);
        try {
            const { suggestions } = yield service.fetchAutocompleteSuggestions({
                input: query,
                sessionToken: sessionTokenRef.current,
                includedPrimaryTypes: INCLUDED_TYPES,
                includedRegionCodes: INCLUDED_REGIONS,
            });
            setSuggestions(suggestions);
        }
        catch (_a) {
            setSuggestions([]);
        }
        finally {
            setLoading(false);
        }
    });
    const scheduleFetch = (query) => {
        if (debounceRef.current)
            window.clearTimeout(debounceRef.current);
        debounceRef.current = window.setTimeout(() => fetchSuggestions(query), DEBOUNCE_MS);
    };
    const selectSuggestion = (s) => __awaiter(void 0, void 0, void 0, function* () {
        setOpen(false);
        const place = s.placePrediction.toPlace();
        try {
            yield place.fetchFields({ fields: ['addressComponents'] });
            const parsed = parseComponents(place.addressComponents || []);
            props.onChange(parsed.address || s.placePrediction.text.text);
            props.onPlaceSelected(parsed);
        }
        catch (_a) {
            // Fallback: use raw prediction text if the details lookup fails.
            props.onChange(s.placePrediction.text.text);
        }
        // Session ends after a place is selected; the next keystroke starts a fresh one.
        sessionTokenRef.current = null;
    });
    return _jsx(_Fragment, { children: _jsxs("div", Object.assign({ ref: containerRef, className: `form-group floating-label w-100 position-relative ${props.small ? 'form-group-sm' : ''} ${props.inline ? 'mb-0' : 'mb-3'}` }, (props.required ? { 'required': true } : {}), { children: [_jsx("label", { htmlFor: id, className: floating || !!props.value ? 'floating' : '', children: props.children }), _jsx("div", { className: `input-group ${props.small ? 'input-group-sm' : ''}`, children: _jsx("input", { id: id, type: "text", autoComplete: "off", value: props.value, onChange: (e) => {
                            props.onChange(e.target.value);
                            if (enabled) {
                                setOpen(true);
                                scheduleFetch(e.target.value);
                            }
                        }, onFocus: () => { setFloating(true); if (enabled && props.value)
                            setOpen(true); }, onBlur: () => setFloating(false), className: `form-control ${props.small ? 'form-control-sm' : ''} ${props.error ? 'is-invalid' : ''}`, disabled: props.disabled, required: props.required, autoFocus: props.autoFocus }) }), open && enabled && (loading || suggestions.length > 0) && _jsxs("ul", { className: "list-group position-absolute w-100 mt-1", style: { zIndex: 1050, maxHeight: 240, overflowY: 'auto', top: '100%' }, children: [loading && _jsx("li", { className: "list-group-item text-muted small", children: "Searching..." }), !loading && suggestions.map((s) => _jsx("li", { className: "list-group-item list-group-item-action small", style: { cursor: 'pointer' }, onMouseDown: (e) => { e.preventDefault(); selectSuggestion(s); }, children: s.placePrediction.text.text }, s.placePrediction.placeId))] })] })) });
};
